import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Button,
  Typography,
  message,
  Tag,
  Steps,
  Card,
  Tabs,
  Empty,
  Space,
} from 'antd';
import {
  AudioOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  VideoCameraOutlined,
  SaveOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { SourceEpisode, AudioAsset } from '../../types';
import { sourceEpisodesApi } from '../../services/api/sourceEpisodes';
import { audioLibraryApi } from '../../services/audioLibraryApi';
import { useApi } from '../../hooks';
import { SimpleVideoPlayerHandle } from '../../features/video-editor/components/SimpleVideoPlayer';
import type { Panel as VideoEditorPanel } from '../../features/video-editor/types';
import { calculateVideoSync } from '../../features/video-editor/compositions/SimpleSlideshow';
import { SubtitleEditorPanel } from '../../components/SubtitleEditorPanel';
import {
  TimingTabPanel,
  MotionTabPanel,
  SfxTabPanel,
  VideoEditorLeftPanel,
} from './video-editor/index';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

const getAudioStatusTag = (status: string | undefined) => {
  switch (status) {
    case 'generating':
      return <Tag icon={<SyncOutlined spin />} color="processing">Generating</Tag>;
    case 'ready':
      return <Tag icon={<CheckCircleOutlined />} color="success">Ready</Tag>;
    case 'error':
      return <Tag icon={<CloseCircleOutlined />} color="error">Error</Tag>;
    default:
      return <Tag color="default">No Audio</Tag>;
  }
};

interface VideoEditorModalProps {
  visible: boolean;
  episode: SourceEpisode;
  storyId: string;
  videoForm?: 'LONG' | 'SHORT';
  onClose: () => void;
  onSuccess: (updatedEpisode?: SourceEpisode) => void;
}

const VideoEditorModal: React.FC<VideoEditorModalProps> = ({
  visible,
  episode,
  storyId,
  videoForm = 'LONG',
  onClose,
  onSuccess,
}) => {
  const aspectRatioProp: '16:9' | '9:16' = videoForm === 'SHORT' ? '9:16' : '16:9';
  // State management
  const [localEpisode, setLocalEpisode] = useState<SourceEpisode>(episode);
  const [panelsMetadata, setPanelsMetadata] = useState<any[]>([]);
  const [editedPanels, setEditedPanels] = useState<any[]>([]);
  const [debouncedEditedPanels, setDebouncedEditedPanels] = useState<any[]>([]);
  const [selectedPanelId, setSelectedPanelId] = useState<string | null>(null);
  const [selectedMotionIndex, setSelectedMotionIndex] = useState<number | null>(null);
  const [selectedSoundIndex, setSelectedSoundIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<string>('motion');
  const voiceAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  
  // Analysis states
  const [isProcessingAudio, setIsProcessingAudio] = useState(false);
  const [chunkSize, setChunkSize] = useState(10);
  const [isAnalyzingEffects, setIsAnalyzingEffects] = useState(false);
  
  // Video generation
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [videoStatus, setVideoStatus] = useState<string>('not_generated');
  const [renderJobId, setRenderJobId] = useState<string | null>(episode.videoJobId || null);
  const [renderProgress, setRenderProgress] = useState<number>(0);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Audio editing
  const [editedBgmId, setEditedBgmId] = useState<string | undefined>(episode.backgroundMusicId);
  const [editedBgmVolume, setEditedBgmVolume] = useState<number>(
    episode.backgroundVolume ?? 0.25
  );
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Player controls
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const currentTimeRef = useRef(0); // Use ref to avoid re-renders
  const [mutedTracks, setMutedTracks] = useState({ voice: false, bgm: false, sfx: false });
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [audioDuration, setAudioDuration] = useState<number | null>(null);
  const playerRef = useRef<SimpleVideoPlayerHandle>(null);
  const timeTrackingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // 🆕 Epic 6: Subtitle states
  const [showSubtitles, setShowSubtitles] = useState(true);
  const [selectedSubtitleLang, setSelectedSubtitleLang] = useState<string>('en');
  const [currentSubtitle, setCurrentSubtitle] = useState<string>('');
  const [rightPanelTab, setRightPanelTab] = useState<'panel' | 'subtitle'>('panel');

  // Fetch data
  // console.log('[VideoEditor] Panels metadata:', {
  //   panelsMetadataCount: panelsMetadata.length,
  //   firstMetadata: panelsMetadata[0],
  // });

  const { data: musicData, refresh: refreshMusic } = useApi(
    useCallback(() => audioLibraryApi.findMany({ type: 'music', status: 'ACTIVE', pageSize: 500 }), []),
    { immediate: visible }
  );
  const musicList = React.useMemo(() => musicData?.data || [], [musicData]);

  const { data: sfxData, refresh: refreshSfx } = useApi(
    useCallback(() => audioLibraryApi.findMany({ type: 'sound_effect', status: 'ACTIVE', pageSize: 500 }), []),
    { immediate: visible }
  );
  const sfxList = React.useMemo(() => sfxData?.data || [], [sfxData]);

  const refreshAudioLibrary = useCallback(() => {
    refreshMusic();
    refreshSfx();
  }, [refreshMusic, refreshSfx]);

  // Sync data - ALWAYS reset when modal opens or episode changes
  useEffect(() => {
    if (visible && episode.panels) {
      setLocalEpisode(episode);
      setEditedBgmId(episode.backgroundMusicId);
      setEditedBgmVolume(episode.backgroundVolume ?? 0.25);
      
      // Sync video status
      setVideoStatus(episode.videoStatus || 'not_generated');
      
      // Sync playback speed from DB
      setPlaybackSpeed(episode.speedMultiplier ?? 1);
      
      // If video is processing, start polling
      if (episode.videoJobId && (episode.videoStatus === 'queued' || episode.videoStatus === 'processing')) {
        setRenderJobId(episode.videoJobId);
        setIsGeneratingVideo(true);
        startPollingJobStatus(episode.videoJobId);
      }

      console.log('[VideoEditor] Syncing data from episode.panels:', episode.panels.map(p => ({
        panelId: p.panelId,
        start: p.startTime,
        end: p.endTime,
        duration: (p.endTime && p.startTime) ? (p.endTime - p.startTime) : 0
      })));
      
      const panelsData = [...episode.panels];
      setPanelsMetadata(panelsData);
      setEditedPanels(panelsData);
      // Set debouncedEditedPanels immediately on sync (no debounce for initial load)
      setDebouncedEditedPanels(panelsData);
      setHasChanges(false);
      
      // Auto-select first panel and set tab to "time"
      if (episode.panels.length > 0) {
        setSelectedPanelId(episode.panels[0].panelId);
        setActiveTab('time');
        setSelectedMotionIndex(null);
        setSelectedSoundIndex(null);
      }
      
      // Load audio duration
      if (episode.masterAudioUrl) {
        const audio = new Audio(episode.masterAudioUrl);
        audio.addEventListener('loadedmetadata', () => {
          console.log('[VideoEditor] Audio duration loaded:', audio.duration);
          setAudioDuration(audio.duration);
        });
        audio.addEventListener('error', (e) => {
          console.error('[VideoEditor] Failed to load audio:', e);
          setAudioDuration(null);
        });
      } else {
        setAudioDuration(null);
      }
    } else if (visible && !episode.panels) {
      // Clear all if no panels
      setPanelsMetadata([]);
      setEditedPanels([]);
      setDebouncedEditedPanels([]);
      setAudioDuration(null);
    }
  }, [visible, episode]);
  
  // Debounce editedPanels updates to prevent video stuttering during edits
  // But skip if this is the initial sync (checked by comparing with panelsMetadata)
  useEffect(() => {
    // Skip debounce if panels are empty (initial state)
    if (editedPanels.length === 0) {
      return;
    }
    
    // Skip debounce if this is initial sync (editedPanels === panelsMetadata)
    if (JSON.stringify(editedPanels) === JSON.stringify(panelsMetadata)) {
      return;
    }
    
    const timer = setTimeout(() => {
      console.log('[VideoEditor] Debouncing editedPanels update...');
      setDebouncedEditedPanels([...editedPanels]);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [editedPanels]);

  // Calculate durations (memoized to prevent re-renders)
  const totalDuration = React.useMemo(() => {
    const total = editedPanels.reduce((acc, pm) => acc + (pm.endTime - pm.startTime), 0);
    console.log('[VideoEditor] Total duration:', total, 'from', editedPanels.length, 'panels');
    return total;
  }, [editedPanels]);
  
  const videoEditorPanels: VideoEditorPanel[] = React.useMemo(() => {
    console.log('[VideoEditor] videoEditorPanels RECALCULATING from debouncedEditedPanels:'); 
    debouncedEditedPanels.forEach((p, idx) => {
      console.log(`  Panel ${idx}: ${p.startTime?.toFixed(2)}s - ${p.endTime?.toFixed(2)}s = ${(p.endTime - p.startTime)?.toFixed(2)}s`);
    });
    
    const result = debouncedEditedPanels.map((panel) => {
      if (!panel.imageUrl) {
        console.warn('[VideoEditor] Panel missing imageUrl:', panel.panelId, panel);
      }
      
      return {
        id: panel.panelId,
        imagePath: panel.imageUrl || '',
        caption: panel.caption || '',
        backgroundColor: '#000000',
        startTime: panel.startTime, // Pass actual audio timestamp
        endTime: panel.endTime, // Pass actual audio timestamp
        motionEffects: panel.motionEffects || [],
        soundEffects: panel.soundEffects || [],
        // Epic 7: Video panel fields
        mediaType: panel.mediaType || 'IMAGE',
        videoUrl: panel.videoUrl || undefined,
        videoDuration: panel.videoDuration || undefined,
        firstFrameUrl: panel.firstFrameUrl || undefined,
      };
    });
    
    console.log('[VideoEditor] videoEditorPanels CREATED:', result.length, 'panels');
    return result;
  }, [debouncedEditedPanels]);

  const panelDurations = React.useMemo(() => {
    console.log('[VideoEditor] Calculating panelDurations from debouncedEditedPanels:', debouncedEditedPanels.length, 'panels');
    
    const durations = debouncedEditedPanels.map((pm, idx) => {
      const startTime = typeof pm.startTime === 'number' ? pm.startTime : parseFloat(pm.startTime || '0');
      const endTime = typeof pm.endTime === 'number' ? pm.endTime : parseFloat(pm.endTime || '0');
      
      const duration = (endTime && startTime !== undefined) ? (endTime - startTime) * 1000 : 5000;
      console.log(`  Panel ${idx} duration: ${duration}ms (${startTime}s - ${endTime}s)`);
      
      return duration;
    });
    
    console.log('[VideoEditor] Final panelDurations:', durations, 'Audio duration:', audioDuration);
    return durations;
  }, [debouncedEditedPanels, audioDuration]);

  const backgroundMusicUrl = React.useMemo(() => 
    editedBgmId ? musicList.find((m: AudioAsset) => m.id === editedBgmId)?.url : undefined,
    [editedBgmId, musicList]
  );

  // Progress calculation
  const hasTimestamps = panelsMetadata.length > 0 && panelsMetadata.some(p => 
    p.startTime !== undefined && p.endTime !== undefined && p.endTime > 0
  );
  const hasMotion = panelsMetadata.some(p => p.motionEffects && p.motionEffects.length > 0);
  const hasAudio = !!(editedBgmId || editedPanels.some(p => p.soundEffects && p.soundEffects.length > 0));
  const currentStep = !hasTimestamps ? 0 : (!hasMotion && !hasAudio) ? 1 : 2;


  // Handlers
  const handleProcessAudio = async () => {
    setIsProcessingAudio(true);
    const msgKey = 'process-audio';
    message.loading({ content: 'Processing audio in chunks...', key: msgKey, duration: 0 });
    try {
      const result = await sourceEpisodesApi.processAudio(storyId, localEpisode.id, chunkSize);
      
      // Epic 260421: Fetch enriched episode detail to get panel video fields
      const enrichedResult = await sourceEpisodesApi.getEpisodeDetail(localEpisode.id);
      const episodeData = enrichedResult.data || result.data;

      message.success({ content: '✅ Audio processed successfully!', key: msgKey });
      
      // Update local state
      setLocalEpisode(episodeData);
      if (episodeData?.panels) {
        setPanelsMetadata([...episodeData.panels]);
        setEditedPanels([...episodeData.panels]);
      }
      
      onSuccess(episodeData);
    } catch (error: any) {
      const errorMsg = error.response?.data?.messages?.[0] || error.message || 'Unknown error';
      message.error({ content: 'Failed to process audio: ' + errorMsg, key: msgKey });
    } finally {
      setIsProcessingAudio(false);
    }
  };


  // Analysis handlers
  const handleAnalyzeMotionOnly = async () => {
    setIsAnalyzingEffects(true);
    try {
      const result = await sourceEpisodesApi.analyzeMotion(storyId, localEpisode.id);
      message.success('✅ Motion effects analyzed successfully!');
      
      // Update local state
      setLocalEpisode(result.data);
      if (result?.data?.panels) {
        setPanelsMetadata([...result.data.panels]);
        setEditedPanels([...result.data.panels]);
      }
      
      setHasChanges(false);
      onSuccess();
    } catch (error: any) {
      message.error('Failed to analyze motion: ' + error.message);
    } finally {
      setIsAnalyzingEffects(false);
    }
  };

  const handleAnalyzeMusicOnly = async () => {
    setIsAnalyzingEffects(true);
    try {
      const result = await sourceEpisodesApi.analyzeMusic(storyId, localEpisode.id);
      message.success('✅ Background Music analyzed successfully!');
      
      // Update local state
      setLocalEpisode(result.data);
      if (result?.data?.backgroundMusicId) {
        setEditedBgmId(result.data.backgroundMusicId);
        setEditedBgmVolume(result.data.backgroundVolume ?? 0.25);
      }
      
      setHasChanges(false);
      onSuccess();
    } catch (error: any) {
      message.error('Failed to analyze music: ' + error.message);
    } finally {
      setIsAnalyzingEffects(false);
    }
  };

  const handleAnalyzeSfxOnly = async () => {
    setIsAnalyzingEffects(true);
    try {
      const result = await sourceEpisodesApi.analyzeSfx(storyId, localEpisode.id);
      message.success('✅ Sound Effects analyzed successfully!');
      
      // Update local state
      setLocalEpisode(result.data);
      if (result?.data?.panels) {
        setPanelsMetadata([...result.data.panels]);
        setEditedPanels([...result.data.panels]);
      }
      
      setHasChanges(false);
      onSuccess();
    } catch (error: any) {
      message.error('Failed to analyze SFX: ' + error.message);
    } finally {
      setIsAnalyzingEffects(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      // Save BGM (including null/undefined to clear it)
      await sourceEpisodesApi.updateBackgroundMusic(
        localEpisode.id, 
        editedBgmId || null, 
        editedBgmVolume
      );

      // Save speed multiplier
      await sourceEpisodesApi.updateEpisode(localEpisode.id, {
        speedMultiplier: playbackSpeed
      });
      
      // Save panels (motion + SFX + subtitleEntries)
      await sourceEpisodesApi.updatePanelsData(localEpisode.id, editedPanels);
      
      // Only regenerate VTT if subtitleEntries actually changed vs original data
      const hasSubtitleChanges = editedPanels.some((panel, index) => {
        const original = panelsMetadata[index];
        return JSON.stringify(panel.subtitleEntries) !== JSON.stringify(original?.subtitleEntries);
      });
      if (hasSubtitleChanges) {
        try {
          // Call backend to regenerate VTT files from updated subtitleEntries
          await sourceEpisodesApi.regenerateSubtitles(localEpisode.id);
          message.success('Changes saved and subtitles regenerated!');
        } catch (error: any) {
          message.warning('Changes saved but subtitle regeneration failed: ' + error.message);
        }
      } else {
        message.success('Changes saved successfully!');
      }
      
      setHasChanges(false);
      onSuccess();
    } catch (error: any) {
      message.error('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateVideo = async () => {
    setIsGeneratingVideo(true);
    setVideoStatus('queued');
    setRenderProgress(0);
    
    try {
      const panelSyncData = editedPanels
        .filter(p => p.mediaType === 'VIDEO' && p.videoUrl)
        .map(p => {
          const audioDuration = (p.endTime ?? 0) - (p.startTime ?? 0);
          const syncResult = calculateVideoSync(audioDuration, p.videoDuration ?? 0);
          return {
            panelId: p.panelId,
            videoSyncStrategy: syncResult.strategy,
            videoPlaybackRate: syncResult.playbackRate,
            videoEndAt: syncResult.endAt,
            videoPadBeforeSec: syncResult.padBeforeSec,
            videoPadAfterSec: syncResult.padAfterSec,
          };
        });

      const result = await sourceEpisodesApi.generateVideo(localEpisode.id, panelSyncData);
      
      if (result.data.success && result.data.jobId) {
        setRenderJobId(result.data.jobId);
        message.success('Video render job queued! Rendering in background...');
        
        // Start polling job status
        startPollingJobStatus(result.data.jobId);
      } else {
        throw new Error(result.data.error || 'Failed to queue render job');
      }
    } catch (error: any) {
      message.error('Failed to queue video render: ' + error.message);
      setVideoStatus('failed');
      setIsGeneratingVideo(false);
    }
  };

  const startPollingJobStatus = useCallback((jobId: string) => {
    // Clear existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    // Poll every 2 seconds
    pollIntervalRef.current = setInterval(async () => {
      try {
        const statusResult = await sourceEpisodesApi.getVideoRenderJobStatus(jobId);
        const jobStatus = statusResult.data;
        
        // Map job status to episode status (pending → queued for UI consistency)
        const mappedStatus = jobStatus.status === 'pending' ? 'queued' : jobStatus.status;
        setVideoStatus(mappedStatus);
        setRenderProgress(jobStatus.progress || 0);
        
        // Stop polling when completed or failed
        if (jobStatus.status === 'completed' || jobStatus.status === 'failed') {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          
          setIsGeneratingVideo(false);
          
          if (jobStatus.status === 'completed') {
            message.success('Video rendered successfully!');
            onSuccess(); // Reload episode data
          } else {
            message.error(`Video render failed: ${jobStatus.errorMessage || 'Unknown error'}`);
          }
        }
      } catch (error: any) {
        console.error('[VideoEditor] Error polling job status:', error);
      }
    }, 2000);
  }, [onSuccess]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Player controls
  const handlePlayPause = () => {
    // console.log('[VideoEditor] handlePlayPause called - current isPlaying:', isPlaying);
    setIsPlaying(!isPlaying);
    if (playerRef.current) {
      if (isPlaying) {
        // console.log('[VideoEditor] Pausing player');
        playerRef.current.pause();
      } else {
        // console.log('[VideoEditor] Playing player');
        playerRef.current.play();
      }
    }
  };

  const handlePrevPanel = () => {
    const currentIndex = panelsMetadata.findIndex(p => p.panelId === selectedPanelId);
    if (currentIndex > 0) {
      const prevPanel = panelsMetadata[currentIndex - 1];
      setSelectedPanelId(prevPanel.panelId);
      // Seek to panel start
      if (playerRef.current) {
        const targetFrame = Math.floor(prevPanel.startTime * 60);
        playerRef.current.seekTo(targetFrame);
      }
    }
  };

  const handleNextPanel = () => {
    const currentIndex = panelsMetadata.findIndex(p => p.panelId === selectedPanelId);
    if (currentIndex < panelsMetadata.length - 1) {
      const nextPanel = panelsMetadata[currentIndex + 1];
      setSelectedPanelId(nextPanel.panelId);
      // Seek to panel start
      if (playerRef.current) {
        const targetFrame = Math.floor(nextPanel.startTime * 60);
        playerRef.current.seekTo(targetFrame);
      }
    }
  };

  const handleSeek = (time: number) => {
    // console.log('[VideoEditor] handleSeek:', time.toFixed(2));
    currentTimeRef.current = time;
    setCurrentTime(time);
    const originalTime = time * playbackSpeed;
    const panel = panelsMetadata.find(p => originalTime >= p.startTime && originalTime <= p.endTime);
    if (panel) {
      setSelectedPanelId(panel.panelId);
    }
    // Seek player to frame
    if (playerRef.current) {
      const targetFrame = Math.floor(time * 60); // fps = 60
      playerRef.current.seekTo(targetFrame);
    }
  };

  const handleMotionSelect = (panelId: string, motionIndex: number) => {
    setSelectedPanelId(panelId);
    setSelectedMotionIndex(motionIndex);
    setActiveTab('motion');
    
    // Auto-scroll to motion effect after a short delay
    setTimeout(() => {
      const element = document.getElementById(`motion-effect-${motionIndex}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleSoundSelect = (panelId: string, soundIndex: number) => {
    setSelectedPanelId(panelId);
    setSelectedSoundIndex(soundIndex);
    setActiveTab('sfx');
    
    // Auto-scroll to sound effect after a short delay
    setTimeout(() => {
      const element = document.getElementById(`sound-effect-${soundIndex}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleToggleMute = (track: 'voice' | 'bgm' | 'sfx') => {
    setMutedTracks(prev => ({ ...prev, [track]: !prev[track] }));
  };

  // Audio editing handlers
  const handleBgmChange = (value: string | undefined) => {
    setEditedBgmId(value);
    setHasChanges(true);
  };

  const handleBgmVolumeChange = (value: number | null) => {
    if (value !== null) {
      setEditedBgmVolume(value);
      setHasChanges(true);
    }
  };

  const handlePlaybackSpeedChange = (value: number | null) => {
    if (value !== null) {
      setPlaybackSpeed(value);
      setHasChanges(true);
    }
  };

  const handleAddSfx = (panelIndex: number) => {
    const newPanels = [...editedPanels];
    if (!newPanels[panelIndex].soundEffects) {
      newPanels[panelIndex].soundEffects = [];
    }
    newPanels[panelIndex].soundEffects.push({
      id: `sfx-${Date.now()}`,
      audioId: sfxList[0]?.id || '',
      audioName: sfxList[0]?.name || '',
      audioUrl: sfxList[0]?.url || '',
      triggerTime: 0,
      duration: 1.5,
      volume: 0.5,
      reasoning: ''
    });
    setEditedPanels(newPanels);
    setHasChanges(true);
  };

  const handleEditSfx = (panelIndex: number, sfxIndex: number, updates: any) => {
    const newPanels = [...editedPanels];
    if (updates.audioId) {
      const selectedSfx = sfxList.find((s: AudioAsset) => s.id === updates.audioId);
      if (selectedSfx) {
        updates.audioName = selectedSfx.name;
        updates.audioUrl = selectedSfx.url;
      }
    }
    newPanels[panelIndex].soundEffects[sfxIndex] = {
      ...newPanels[panelIndex].soundEffects[sfxIndex],
      ...updates
    };
    setEditedPanels(newPanels);
    setHasChanges(true);
  };

  const handleDeleteSfx = (panelIndex: number, sfxIndex: number) => {
    const newPanels = [...editedPanels];
    newPanels[panelIndex].soundEffects.splice(sfxIndex, 1);
    setEditedPanels(newPanels);
    setHasChanges(true);
  };

  /** Kéo thả / resize SFX trên timeline (có thể chuyển sang panel khác) */
  const handleSfxTimelineUpdate = useCallback(
    (u: {
      fromPanelId: string;
      sfxIndex: number;
      sfxId?: string;
      triggerTime: number;
      duration: number;
      targetPanelId: string;
    }) => {
      setEditedPanels((prev) => {
        const panels = prev.map((p) => ({
          ...p,
          soundEffects: [...(p.soundEffects || [])],
        }));
        const fromIdx = panels.findIndex((p) => p.panelId === u.fromPanelId);
        if (fromIdx < 0) return prev;

        let sfxIndex = u.sfxIndex;
        if (u.sfxId) {
          const found = panels[fromIdx].soundEffects.findIndex((s: any) => s.id === u.sfxId);
          if (found >= 0) sfxIndex = found;
        }
        const list = panels[fromIdx].soundEffects;
        if (sfxIndex < 0 || sfxIndex >= list.length) return prev;

        const sfx = { ...list[sfxIndex] };

        if (u.fromPanelId === u.targetPanelId) {
          list[sfxIndex] = {
            ...sfx,
            triggerTime: u.triggerTime,
            duration: u.duration,
          };
          return panels;
        }

        list.splice(sfxIndex, 1);
        const toIdx = panels.findIndex((p) => p.panelId === u.targetPanelId);
        if (toIdx < 0) return prev;
        if (!panels[toIdx].soundEffects) panels[toIdx].soundEffects = [];
        panels[toIdx].soundEffects.push({
          ...sfx,
          triggerTime: u.triggerTime,
          duration: u.duration,
        });
        return panels;
      });
      setHasChanges(true);
    },
    []
  );

  const handleEditMotion = (panelIndex: number, motionIndex: number, updates: any) => {
    const newPanels = [...editedPanels];
    if (!newPanels[panelIndex].motionEffects) {
      newPanels[panelIndex].motionEffects = [];
    }
    newPanels[panelIndex].motionEffects[motionIndex] = {
      ...newPanels[panelIndex].motionEffects[motionIndex],
      ...updates
    };
    setEditedPanels(newPanels);
    setHasChanges(true);
  };

  const handleDeleteMotion = (panelIndex: number, motionIndex: number) => {
    const newPanels = [...editedPanels];
    newPanels[panelIndex].motionEffects.splice(motionIndex, 1);
    setEditedPanels(newPanels);
    setHasChanges(true);
  };

  const handleAddMotion = (panelIndex: number) => {
    const newPanels = [...editedPanels];
    if (!newPanels[panelIndex].motionEffects) {
      newPanels[panelIndex].motionEffects = [];
    }
    const panelDuration = newPanels[panelIndex].endTime - newPanels[panelIndex].startTime;
    newPanels[panelIndex].motionEffects.push({
      id: `motion-${Date.now()}`,
      type: 'zoom-in',
      startOffset: 0,
      duration: Math.min(panelDuration, 3),
      intensity: 1.2,
      focusPoint: { x: 0.5, y: 0.5 },
      overlay: false,
      confidence: 1,
      reasoning: 'Manual effect'
    });
    setEditedPanels(newPanels);
    setHasChanges(true);
  };

  // Keyboard shortcuts
  useEffect(() => {
    if (!visible) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      if (e.code === 'Space') {
        e.preventDefault();
        handlePlayPause();
      }
      if (e.code === 'ArrowLeft') {
        e.preventDefault();
        e.shiftKey ? handlePrevPanel() : handleSeek(Math.max(0, currentTime - 1));
      }
      if (e.code === 'ArrowRight') {
        e.preventDefault();
        e.shiftKey ? handleNextPanel() : handleSeek(Math.min(totalDuration, currentTime + 1));
      }
      if ((e.metaKey || e.ctrlKey) && e.code === 'KeyS') {
        e.preventDefault();
        if (hasChanges) handleSaveChanges();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [visible, isPlaying, currentTime, selectedPanelId, hasChanges, totalDuration]);

  // Track player time + update subtitle
  useEffect(() => {
    // console.log('[VideoEditor] useEffect triggered - visible:', visible, 'isPlaying:', isPlaying);
    
    if (!visible) return;

    // Clear existing interval first
    if (timeTrackingIntervalRef.current) {
      console.log('[VideoEditor] Clearing existing interval');
      clearInterval(timeTrackingIntervalRef.current);
      timeTrackingIntervalRef.current = null;
    }

    // Only start interval if playing
    if (isPlaying && playerRef.current) {
      // console.log('[VideoEditor] Starting new interval');
      let lastStateUpdateTime = 0;
      let intervalCount = 0;

      timeTrackingIntervalRef.current = setInterval(() => {
        intervalCount++;
        if (playerRef.current) {
          const currentFrame = playerRef.current.getCurrentFrame();
          const time = currentFrame / 60; // fps = 60
          
          // Always update ref (no re-render)
          currentTimeRef.current = time;
          
          // 🆕 Epic 6: Update current subtitle
          if (showSubtitles && episode.subtitles) {
            const subtitlesData = episode.subtitles as any;
            if (subtitlesData?.files?.[selectedSubtitleLang]) {
              // Find subtitle entry for current time — convert to original domain before comparing
              const originalTime = time * playbackSpeed;
              let foundSubtitle = '';
              for (const panel of editedPanels) {
                const entries = panel.subtitleEntries?.[selectedSubtitleLang];
                if (entries && Array.isArray(entries)) {
                  for (const entry of entries) {
                    if (originalTime >= entry.startTime && originalTime < entry.endTime) {
                      foundSubtitle = entry.text;
                      break;
                    }
                  }
                }
                if (foundSubtitle) break;
              }
              setCurrentSubtitle(foundSubtitle);
            }
          }
          
          // if (intervalCount % 10 === 0) {
          //   console.log('[VideoEditor] Interval tick', intervalCount, 'time:', time.toFixed(2));
          // }
          
          // Only update STATE every 0.5s (reduce re-renders drastically)
          if (Math.abs(time - lastStateUpdateTime) >= 0.5) {
            // console.log('[VideoEditor] Updating currentTime STATE:', time.toFixed(2));
            setCurrentTime(time);
            lastStateUpdateTime = time;
          }
          
          // Skip auto-select during playback to prevent re-renders
          // User can still manually select panels or use prev/next buttons
        }
      }, 200); // Poll every 200ms
    }

    return () => {
      // console.log('[VideoEditor] Cleanup - clearing interval');
      if (timeTrackingIntervalRef.current) {
        clearInterval(timeTrackingIntervalRef.current);
        timeTrackingIntervalRef.current = null;
      }
    };
  }, [visible, isPlaying, panelsMetadata, showSubtitles, selectedSubtitleLang, editedPanels, episode.subtitles]);

  // Get selected panel data
  const selectedPanelIndex = panelsMetadata.findIndex(p => p.panelId === selectedPanelId);
  const selectedPanel = selectedPanelIndex >= 0 ? panelsMetadata[selectedPanelIndex] : null;
  const selectedEditedPanel = selectedPanelIndex >= 0 ? editedPanels[selectedPanelIndex] : null;

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            🎬 Video Editor: {episode.name}
          </Title>
          <Steps
            current={currentStep}
            size="small"
            style={{ marginTop: 12 }}
            items={[
              {
                title: 'Timestamps',
                icon: <ClockCircleOutlined />,
                status: hasTimestamps ? 'finish' : currentStep === 0 ? 'process' : 'wait',
              },
              {
                title: 'Effects',
                icon: <ThunderboltOutlined />,
                status: (hasMotion || hasAudio) ? 'finish' : currentStep === 1 ? 'process' : 'wait',
              },
              {
                title: 'Audio',
                icon: <AudioOutlined />,
                status: episode.audioStatus === 'ready' ? 'finish' : 'wait',
              },
              {
                title: 'Export',
                icon: <VideoCameraOutlined />,
                status: videoStatus === 'completed' ? 'finish' : currentStep === 2 ? 'process' : 'wait',
              },
            ]}
          />
        </div>
      }
      open={visible}
      onCancel={onClose}
      width="98%"
      style={{ top: 10, maxWidth: 1800 }}
      styles={{ body: { height: 'calc(100vh - 200px)', overflow: 'hidden', padding: 0 } }}
      footer={[
        <Button key="close" onClick={onClose}>
          ← Back to Episodes
        </Button>,
        <Button
          key="save"
          icon={<SaveOutlined />}
          onClick={handleSaveChanges}
          disabled={!hasChanges || saving}
          loading={saving}
          type="primary"
        >
          {hasChanges && <Tag color="orange" style={{ marginRight: 4 }}>●</Tag>}
          Save Changes
        </Button>,
        <Button
          key="generate"
          icon={<VideoCameraOutlined />}
          onClick={handleGenerateVideo}
          disabled={isGeneratingVideo}
          loading={isGeneratingVideo}
          type="primary"
          size="large"
          style={{ background: '#52c41a', borderColor: '#52c41a' }}
        >
          🎬 Generate Video
        </Button>,
      ]}
    >
      {/* CapCut-style Layout: Horizontal 3-column */}
      <div style={{ display: 'flex', height: '100%', gap: '0', backgroundColor: '#1a1a1a' }}>
        
        {/* LEFT SIDEBAR: Panels Library (12%) */}
        <div style={{ 
          width: '12%', 
          minWidth: '180px',
          backgroundColor: '#2a2a2a', 
          borderRight: '1px solid #3a3a3a',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '12px', borderBottom: '1px solid #3a3a3a' }}>
            <Text strong style={{ color: '#fff', fontSize: '13px' }}>📚 Panels</Text>
            <Tag color="blue" style={{ marginLeft: 8, fontSize: '10px' }}>{panelsMetadata.length}</Tag>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
            {panelsMetadata.length > 0 ? (
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                {panelsMetadata.map((pm, idx) => {
                  const isSelected = pm.panelId === selectedPanelId;
                  const isPlaying = currentTime >= pm.startTime && currentTime <= pm.endTime;
                  const hasMotion = editedPanels[idx]?.motionEffects?.length > 0;
                  const hasSfx = editedPanels[idx]?.soundEffects?.length > 0;

                  return (
                    <Card
                      key={pm.panelId}
                      size="small"
                      hoverable
                      onClick={() => setSelectedPanelId(pm.panelId)}
                      style={{
                        cursor: 'pointer',
                        borderLeft: isPlaying ? '3px solid #52c41a' : (isSelected ? '2px solid #1890ff' : '1px solid #3a3a3a'),
                        borderRight: isSelected ? '2px solid #1890ff' : '1px solid #3a3a3a',
                        borderTop: isSelected ? '2px solid #1890ff' : '1px solid #3a3a3a',
                        borderBottom: isSelected ? '2px solid #1890ff' : '1px solid #3a3a3a',
                        backgroundColor: isPlaying ? '#1e3a5f' : '#333',
                      }}
                      styles={{ body: { padding: '6px' } }}
                    >
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {pm.imageUrl && (
                          <img
                            src={pm.imageUrl}
                            alt={`Panel ${idx + 1}`}
                            style={{ width: 40, height: 60, objectFit: 'cover', borderRadius: 4 }}
                          />
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text strong style={{ color: '#fff', fontSize: '11px', display: 'block' }}>
                            #{idx + 1}
                          </Text>
                          <Text style={{ color: '#999', fontSize: '10px', display: 'block' }}>
                            {(pm.endTime - pm.startTime).toFixed(1)}s
                          </Text>
                          <Space size={2}>
                            {hasMotion && <Tag color="purple" style={{ fontSize: '8px', margin: 0, padding: '0 3px' }}>M</Tag>}
                            {hasSfx && <Tag color="orange" style={{ fontSize: '8px', margin: 0, padding: '0 3px' }}>S</Tag>}
                          </Space>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </Space>
            ) : (
              <Empty 
                description="No panels" 
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ marginTop: 40 }}
              />
            )}
          </div>
        </div>

        {/* CENTER + RIGHT SIDEBAR Content */}
        <VideoEditorLeftPanel
          playerRef={playerRef}
          videoEditorPanels={videoEditorPanels}
          panelDurations={panelDurations}
          episode={episode}
          backgroundMusicUrl={backgroundMusicUrl}
          editedBgmVolume={editedBgmVolume}
          aspectRatio={aspectRatioProp}
          mutedTracks={mutedTracks}
          playbackSpeed={playbackSpeed}
          audioDuration={audioDuration}
          hasTimestamps={hasTimestamps}
          showSubtitles={showSubtitles}
          currentSubtitle={currentSubtitle}
          setShowSubtitles={setShowSubtitles}
          selectedSubtitleLang={selectedSubtitleLang}
          setSelectedSubtitleLang={setSelectedSubtitleLang}
          isPlaying={isPlaying}
          handlePlayPause={handlePlayPause}
          handlePrevPanel={handlePrevPanel}
          handleNextPanel={handleNextPanel}
          currentTime={currentTime}
          totalDuration={totalDuration / playbackSpeed}
          handleSeek={handleSeek}
          setPlaybackSpeed={handlePlaybackSpeedChange}
          handleToggleMute={handleToggleMute}
          editedPanels={editedPanels}
          selectedPanelId={selectedPanelId}
          setSelectedPanelId={setSelectedPanelId}
          handleMotionSelect={handleMotionSelect}
          handleSoundSelect={handleSoundSelect}
          handleSfxTimelineUpdate={handleSfxTimelineUpdate}
          currentStep={currentStep}
          hasMotion={hasMotion}
          hasAudio={hasAudio}
          localEpisode={localEpisode}
          getAudioStatusTag={getAudioStatusTag}
          chunkSize={chunkSize}
          setChunkSize={setChunkSize}
          handleProcessAudio={handleProcessAudio}
          isProcessingAudio={isProcessingAudio}
          handleAnalyzeMotionOnly={handleAnalyzeMotionOnly}
          handleAnalyzeMusicOnly={handleAnalyzeMusicOnly}
          handleAnalyzeSfxOnly={handleAnalyzeSfxOnly}
          isAnalyzingEffects={isAnalyzingEffects}
          editedBgmId={editedBgmId}
          handleBgmChange={handleBgmChange}
          musicList={musicList}
          handleBgmVolumeChange={handleBgmVolumeChange}
          refreshAudioLibrary={refreshAudioLibrary}
          isGeneratingVideo={isGeneratingVideo}
          videoStatus={videoStatus}
          renderProgress={renderProgress}
          handleGenerateVideo={handleGenerateVideo}
        />

        {/* RIGHT SIDEBAR: Properties Panel (22%) - Only Tabs & Bottom Content */}
        <div style={{ 
          width: '22%',
          minWidth: '300px',
          backgroundColor: '#2a2a2a',
          borderLeft: '1px solid #3a3a3a',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Scrollable container for Tabs content */}
          <div style={{ 
            flex: 1,
            overflowY: 'auto',
            padding: '12px'
          }}>
          {/* 🆕 Epic 6: Top-level tabs for Panel Editor vs Subtitle Editor */}
          <Tabs
            activeKey={rightPanelTab}
            onChange={(key) => setRightPanelTab(key as 'panel' | 'subtitle')}
            style={{ 
              padding: '0',
              backgroundColor: '#2a2a2a',
              marginTop: 12
            }}
              items={[
                {
                  key: 'panel',
                  label: '🎬 Panel Editor',
                },
                {
                  key: 'subtitle',
                  label: '📝 Subtitles',
                  disabled: !episode.subtitles || !Object.keys((episode.subtitles as any)?.files || {}).length,
                },
              ]}
            />

            {rightPanelTab === 'panel' && (
                selectedPanel ? (
                  <Card
                    title={<Text style={{ color: '#fff' }}>Panel {selectedPanelIndex + 1} Properties</Text>}
                    size="small"
                    style={{ backgroundColor: '#333', border: '1px solid #3a3a3a' }}
                    styles={{ 
                      header: { backgroundColor: '#3a3a3a', borderBottom: '1px solid #444' },
                      body: { backgroundColor: '#333' }
                    }}
                    extra={
                      <Tag color="blue">
                        {(selectedPanel.endTime - selectedPanel.startTime).toFixed(1)}s
                      </Tag>
                    }
                  >
                    <Tabs 
                      activeKey={activeTab} 
                      onChange={setActiveTab}
                      size="small" 
                      style={{ color: '#fff' }}
                    >
                      {/* Timestamps Tab */}
                      <TabPane tab="⏱️ Time" key="time">
                        <TimingTabPanel
                          episode={episode}
                          selectedPanel={selectedPanel}
                          selectedPanelIndex={selectedPanelIndex}
                          isPlayingVoice={isPlayingVoice}
                          setIsPlayingVoice={setIsPlayingVoice}
                          voiceAudioRef={voiceAudioRef}
                          editedPanels={editedPanels}
                          setEditedPanels={setEditedPanels}
                          panelsMetadata={panelsMetadata}
                          setPanelsMetadata={setPanelsMetadata}
                          setHasChanges={setHasChanges}
                        />
                      </TabPane>

                      {/* Motion Tab */}
                      <TabPane tab="🎬 Motion" key="motion">
                        <MotionTabPanel
                          selectedPanel={selectedPanel}
                          selectedPanelIndex={selectedPanelIndex}
                          selectedMotionIndex={selectedMotionIndex}
                          setSelectedMotionIndex={setSelectedMotionIndex}
                          handleAddMotion={handleAddMotion}
                          handleEditMotion={handleEditMotion}
                          handleDeleteMotion={handleDeleteMotion}
                        />
                      </TabPane>

                      {/* Audio Tab */}
                      <TabPane tab="🔊 SFX" key="sfx">
                        <SfxTabPanel
                          selectedEditedPanel={selectedEditedPanel}
                          selectedPanel={selectedPanel}
                          selectedPanelIndex={selectedPanelIndex}
                          selectedSoundIndex={selectedSoundIndex}
                          setSelectedSoundIndex={setSelectedSoundIndex}
                          sfxList={sfxList}
                          handleAddSfx={handleAddSfx}
                          handleEditSfx={handleEditSfx}
                          handleDeleteSfx={handleDeleteSfx}
                          refreshAudioLibrary={refreshAudioLibrary}
                        />
                      </TabPane>
                    </Tabs>
                  </Card>
                ) : (
                  <Card 
                    size="small"
                    style={{ backgroundColor: '#333', border: '1px solid #3a3a3a' }}
                    styles={{ body: { backgroundColor: '#333' } }}
                  >
                    <Empty 
                      description={<Text style={{ color: '#999' }}>Select a panel to edit</Text>}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </Card>
                )
              )}

              {/* 🆕 Epic 6: Subtitle Editor Panel */}
              {rightPanelTab === 'subtitle' && episode.subtitles && (episode.subtitles as any).files && (
            <SubtitleEditorPanel
              subtitleEntries={
                editedPanels
                  .filter(p => p.subtitleEntries && p.subtitleEntries[selectedSubtitleLang])
                  .flatMap(p => p.subtitleEntries[selectedSubtitleLang])
                  .sort((a, b) => a.startTime - b.startTime)
              }
              currentTime={currentTime}
              onSeek={handleSeek}
              onUpdateEntry={(index, entry) => {
                // Find which panel this subtitle belongs to
                let currentIndex = 0;
                for (let i = 0; i < editedPanels.length; i++) {
                  const panel = editedPanels[i];
                  const entries = panel.subtitleEntries?.[selectedSubtitleLang] || [];
                  
                  if (currentIndex + entries.length > index) {
                    // This panel contains the subtitle
                    const localIndex = index - currentIndex;
                    const updatedEntries = [...entries];
                    updatedEntries[localIndex] = entry;
                    
                    const updatedPanels = [...editedPanels];
                    updatedPanels[i] = {
                      ...panel,
                      subtitleEntries: {
                        ...panel.subtitleEntries,
                        [selectedSubtitleLang]: updatedEntries,
                      },
                    };
                    
                    setEditedPanels(updatedPanels);
                    setHasChanges(true);
                    break;
                  }
                  
                  currentIndex += entries.length;
                }
              }}
              onDeleteEntry={(index) => {
                // Find which panel this subtitle belongs to
                let currentIndex = 0;
                for (let i = 0; i < editedPanels.length; i++) {
                  const panel = editedPanels[i];
                  const entries = panel.subtitleEntries?.[selectedSubtitleLang] || [];
                  
                  if (currentIndex + entries.length > index) {
                    // This panel contains the subtitle
                    const localIndex = index - currentIndex;
                    const updatedEntries = entries.filter((_: any, idx: number) => idx !== localIndex);
                    
                    const updatedPanels = [...editedPanels];
                    updatedPanels[i] = {
                      ...panel,
                      subtitleEntries: {
                        ...panel.subtitleEntries,
                        [selectedSubtitleLang]: updatedEntries,
                      },
                    };
                    
                    setEditedPanels(updatedPanels);
                    setHasChanges(true);
                    break;
                  }
                  
                  currentIndex += entries.length;
                }
              }}
              onAddEntry={(entry) => {
                // Find which panel this time belongs to
                const targetPanel = editedPanels.find(
                  p => entry.startTime >= p.startTime && entry.startTime < p.endTime
                );
                
                if (targetPanel) {
                  const panelIndex = editedPanels.findIndex(p => p.panelId === targetPanel.panelId);
                  const existingEntries = targetPanel.subtitleEntries?.[selectedSubtitleLang] || [];
                  const updatedEntries = [...existingEntries, entry].sort((a, b) => a.startTime - b.startTime);
                  
                  const updatedPanels = [...editedPanels];
                  updatedPanels[panelIndex] = {
                    ...targetPanel,
                    subtitleEntries: {
                      ...targetPanel.subtitleEntries,
                      [selectedSubtitleLang]: updatedEntries,
                    },
                  };
                  
                  setEditedPanels(updatedPanels);
                  setHasChanges(true);
                } else {
                  message.error('Cannot add subtitle: time is outside any panel range');
                }
              }}
              language={selectedSubtitleLang}
            />
          )}
          </div>

        </div>
      </div>
    </Modal>
  );
};

export default VideoEditorModal;
